import React from "react";
import { Link as RouterLink } from "react-router-dom";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { Alert, Autocomplete, Box, Link, Stack, TextField, Typography } from "@mui/material";
import useAsyncRequest from "hooks/useAsyncRequest";
import { getErrorMessage } from "utils/errorHelpers";
import { ContactRole, SalesMotion } from "client/api-v2";
import type { OrderContactPartyDto, OrderResellerDto } from "client/api-v2";
import { MutedPlaceholder } from "components/MutedPlaceholder";
import { billingAccountRoutes } from "areas/billing-accounts/billing-account-routes";
import { BuilderSection } from "areas/orders/builder/BuilderSection";
import type { SectionProps } from "areas/orders/builder/sections/SectionProps";
import type { ContactPartyView, ContactView } from "areas/orders/components/chrome/ContactsHoverCard";
import { useGuardedNavigate } from "areas/orders/hooks/useUnsavedChangesGuard";
import { withReturnToOrder } from "areas/orders/order-routes";
import { getOrderContacts, listResellers } from "areas/orders/services/orderService";

const endCustomerHeading = "End customer";
const resellerHeading = "Reseller (billed party)";
const contactsHeading = "Contacts";

const resellerRenewalWarning =
  "This is a renewal via reseller — confirm both purchasing accounts' contact details are correct " +
  "and up to date before sending a quote.";
const contactsUnavailable = "Contact details are not available yet.";
const contactsAfterSave = "Save the quote to read this account's contacts.";
const moreResellersThanShown = "Only the first matches are shown. Narrow the search to find others.";

function addressLine(party: OrderContactPartyDto): string | undefined {
  const address = party.address;
  if (!address) {
    return undefined;
  }

  const parts = [address.address1, address.address2, address.city, address.state, address.postalCode]
    .map((part) => part?.trim())
    .filter((part): part is string => !!part);

  return parts.length === 0 ? undefined : parts.join(", ");
}

function toContactViews(party: OrderContactPartyDto): ContactView[] {
  return party.contacts.map((contact) => ({
    name: [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim(),
    email: contact.email,
    roles: contact.roles,
    isBilling: contact.roles.includes(ContactRole.Billing),
  }));
}

function toPartyView(party: OrderContactPartyDto, isAvailable: boolean): ContactPartyView {
  return {
    legalName: party.legalName,
    address: addressLine(party),
    isIndividual: false,
    contacts: isAvailable ? toContactViews(party) : [],
    unavailable: !isAvailable,
  };
}

function ContactRow({ contact }: { contact: ContactView }) {
  return (
    <Box sx={{ opacity: contact.isBilling ? 1 : 0.72 }}>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
        <Typography variant="body2">{contact.name}</Typography>
        {contact.isBilling && (
          <Typography variant="overline" color="primary" sx={{ lineHeight: 1 }}>
            Billing contact
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div">
        {contact.email}
        {contact.roles.length > 0 && (
          <>
            <br />
            {contact.roles.join(", ")}
          </>
        )}
      </Typography>
    </Box>
  );
}

function PartyPanel({
  heading,
  party,
  unavailableMessage = contactsUnavailable,
  children,
}: {
  heading: string;
  party: ContactPartyView | null;
  unavailableMessage?: string;
  children?: React.ReactNode;
}) {
  return (
    <Box sx={{ minWidth: 220, flex: 1 }}>
      <Typography variant="overline" color="text.secondary" component="h3">
        {heading}
      </Typography>
      {children}
      {party === null ? (
        <MutedPlaceholder>None</MutedPlaceholder>
      ) : (
        <>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <BusinessOutlinedIcon fontSize="small" sx={{ color: "text.secondary", flex: "none" }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {party.legalName}
            </Typography>
          </Stack>
          {party.address ? (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
              {party.address}
            </Typography>
          ) : null}
          <Typography variant="overline" color="text.secondary" component="div">
            {contactsHeading}
          </Typography>
          {party.unavailable ? (
            <Typography variant="body2" color="text.secondary">
              {unavailableMessage}
            </Typography>
          ) : party.contacts.length === 0 ? (
            <MutedPlaceholder>None</MutedPlaceholder>
          ) : (
            <Stack spacing={0.75}>
              {party.contacts.map((contact) => (
                <ContactRow key={contact.email} contact={contact} />
              ))}
            </Stack>
          )}
        </>
      )}
    </Box>
  );
}

// Why: two accounts can share a legal name, so the code is what tells the options apart and what
// keys them; a repeated key makes React reuse the wrong row.
function ResellerOption({ reseller }: { reseller: OrderResellerDto }) {
  return (
    <Box>
      <Typography variant="body2">{reseller.legalName}</Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ fontFamily: "monospace" }}>
        {reseller.code}
      </Typography>
    </Box>
  );
}

function ResellerPicker({
  selectedCode,
  selectedLegalName,
  onChange,
}: {
  selectedCode: string | null;
  selectedLegalName: string | null;
  onChange: (code: string | null) => void;
}) {
  const [search, setSearch] = React.useState("");
  const { data, loading } = useAsyncRequest(listResellers, search);

  const options = data?.resellers ?? [];
  // Why: the order's own reseller may not be in the current page of matches, and dropping it would
  // show the field as empty while the order still bills that account.
  const selected: OrderResellerDto | null =
    selectedCode === null
      ? null
      : options.find((option) => option.code === selectedCode) ?? {
          code: selectedCode,
          legalName: selectedLegalName ?? selectedCode,
          commonName: null,
        };

  return (
    <Autocomplete
      options={options}
      value={selected}
      loading={loading}
      onChange={(_event, value) => onChange(value === null ? null : value.code)}
      onInputChange={(_event, value) => setSearch(value)}
      getOptionLabel={(option) => option.legalName}
      getOptionKey={(option) => option.code}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      renderOption={({ key, ...optionProps }, option) => (
        <li key={key} {...optionProps}>
          <ResellerOption reseller={option} />
        </li>
      )}
      size="small"
      sx={{ maxWidth: 320, mb: 1 }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Reseller"
          placeholder="Search reseller"
          helperText={data?.hasMoreMatches ? moreResellersThanShown : undefined}
        />
      )}
    />
  );
}

export function BillingAccountsSection({ order, draft, setField, readOnly }: SectionProps) {
  const [isEditing, setIsEditing] = React.useState(false);
  const guardedNavigate = useGuardedNavigate();
  // Why: either account can change without the OD number changing, so both codes key the reload.
  const loadContacts = React.useCallback(
    () => getOrderContacts(order.odNumber),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order.odNumber, order.billingAccountCode, order.resellerBillingAccountCode]
  );
  const { data: contacts, loading, error } = useAsyncRequest(loadContacts);

  const resellerCode = draft.resellerBillingAccountCode;
  const showReseller = resellerCode !== null || isEditing;
  const showRenewalWarning = order.salesMotion === SalesMotion.Renewal && resellerCode !== null;
  const endCustomer: ContactPartyView =
    contacts === undefined
      ? { legalName: order.billingAccountLegalName, isIndividual: false, contacts: [], unavailable: true }
      : toPartyView(contacts.endCustomer, contacts.isAvailable);

  const resellerIsSaved = resellerCode === order.resellerBillingAccountCode;
  const reseller: ContactPartyView | null =
    resellerCode === null
      ? null
      : contacts?.reseller && resellerIsSaved
      ? toPartyView(contacts.reseller, contacts.isAvailable)
      : {
          legalName: (resellerIsSaved ? order.resellerBillingAccountLegalName : null) ?? resellerCode,
          isIndividual: false,
          contacts: [],
          unavailable: true,
        };

  const endCustomerAccountPath = withReturnToOrder(
    billingAccountRoutes.detail(order.billingAccountCode).root,
    order.odNumber
  );
  const resellerAccountPath =
    resellerCode === null ? null : withReturnToOrder(billingAccountRoutes.detail(resellerCode).root, order.odNumber);

  return (
    <BuilderSection
      title="Billing accounts"
      action={
        !readOnly && (
          <Link component="button" type="button" variant="body2" onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? "Done" : "Edit"}
          </Link>
        )
      }
    >
      {showRenewalWarning && <Alert severity="warning">{resellerRenewalWarning}</Alert>}

      {!!error && <Alert severity="error">{getErrorMessage(error)}</Alert>}

      {loading && contacts === undefined ? (
        <Typography variant="body2" color="text.secondary">
          Loading the purchasing accounts…
        </Typography>
      ) : (
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={4}
          divider={<Box sx={{ borderLeft: 1, borderColor: "divider" }} />}
        >
          <PartyPanel heading={endCustomerHeading} party={endCustomer}>
            {isEditing && (
              <Box sx={{ mb: 1 }}>
                <Link
                  component={RouterLink}
                  to={endCustomerAccountPath}
                  onClick={guardedNavigate(endCustomerAccountPath)}
                  variant="caption"
                >
                  Edit billing account for this subscription
                </Link>
              </Box>
            )}
          </PartyPanel>

          {showReseller && (
            <PartyPanel
              heading={resellerHeading}
              party={reseller}
              unavailableMessage={resellerIsSaved ? contactsUnavailable : contactsAfterSave}
            >
              {isEditing && (
                <>
                  <ResellerPicker
                    selectedCode={resellerCode}
                    selectedLegalName={order.resellerBillingAccountLegalName}
                    onChange={(code) => setField("resellerBillingAccountCode", code)}
                  />
                  {resellerAccountPath !== null && (
                    <Box sx={{ mb: 1 }}>
                      <Link
                        component={RouterLink}
                        to={resellerAccountPath}
                        onClick={guardedNavigate(resellerAccountPath)}
                        variant="caption"
                      >
                        Edit billing account details and contacts
                      </Link>
                    </Box>
                  )}
                </>
              )}
            </PartyPanel>
          )}
        </Stack>
      )}
    </BuilderSection>
  );
}
